import * as Sentry from '@sentry/browser';
import AchPopoutForm from '~/javascripts/components/DonateForm/AchPopoutForm';
import AmountButtonsExperiment from '~/javascripts/components/DonateForm/Experiments/AmountButtonsExperiment';
import AmountImpactCalculator from '~/javascripts/components/AmountImpactCalculator';
import AmountStepText from '~/javascripts/components/DonateForm/AmountStepText';
import CalmPaymentTabs from '~/javascripts/components/DonateForm/CalmPaymentTabs.jsx';
import CampaignRedirectBanner from '~/javascripts/components/DonateForm/CampaignRedirectBanner';
import CreditCardIcon from '~/images/icons/credit-card-icon.svg';
import DonateByCheck from '~/javascripts/components/DonateForm/DonateByCheck';
import DonateFormAmountButtons from '~/javascripts/components/DonateForm/AmountButtons';
import DonateFormAmountInput from '~/javascripts/components/DonateForm/AmountInput';
import DonateFormButton from '~/javascripts/components/DonateForm/Button';
import DonateFormDonationFrequencyToggle from '~/javascripts/components/DonateForm/DonationFrequencyToggle';
import DonateFormDonorInfoCollectionFields from '~/javascripts/components/DonateForm/DonorInfoCollectionFields';
import DonateFormDonorInfoPopoutForm from '~/javascripts/components/DonateForm/DonorInfoPopoutForm';
import DonateFormEmailField from '~/javascripts/components/DonateForm/EmailField';
import DonateFormHeader from '~/javascripts/components/DonateForm/Header';
import DonateFormIhoHonoree from '~/javascripts/components/DonateForm/Iho/Honoree';
import DonateFormIhoModal from '~/javascripts/components/DonateForm/Iho/Modal';
import DonateFormIhoToggle from '~/javascripts/components/DonateForm/Iho/Toggle';
import DonateFormSpringMemberExistsMessage from '~/javascripts/components/DonateForm/SpringMemberExistsMessage';
import DonateFormUserCampaignFields from '~/javascripts/components/DonateForm/UserCampaignFields';
import EmbedThankYouSticker from '~/images/icons/100-percent-sticker.svg';
import PaymentRequestForm from '~/javascripts/components/DonateForm/PaymentRequestForm';
import ProcessedByText from '~/javascripts/components/DonateForm/ProcessedByText';
import RecaptchaBadge from '~/javascripts/components/RecaptchaBadge';
import RecaptchaText from '~/javascripts/components/RecaptchaText';
import StripeElements from '~/javascripts/components/DonateForm/StripeElements';
import SubscriptionUpsell from '~/javascripts/components/DonateForm/SubscriptionUpsell';
import cx from 'classnames';
import directDebitLogo from '~/images/icons/direct-debit-logo.svg';
import fakeStripe from '~/javascripts/utils/fakeStripe';
import store from '~/javascripts/store.js';
import t from '~/javascripts/utils/translation';
import uuid from 'uuid/v4';
import { Component } from 'react';
import { Elements, ElementsConsumer } from '@stripe/react-stripe-js';
import { amountWithPrecisionAsRequiredAndComma } from '~/javascripts/utils/numberFormatter';
import { intercept, reaction } from 'mobx';
import { loadStripe } from '@stripe/stripe-js/pure';
import { observer } from 'mobx-react';
import { parsedQueryString } from '~/javascripts/utils/params';
import { pauseForSpecs } from '~/javascripts/utils/testing';
import { sortBy } from 'lodash';

let stripePromise;
const queryParams = parsedQueryString();

const DonateForm = observer(
  class DonateForm extends Component {
    constructor(props) {
      super(props);

      const enableCalmMode = (options = {}) => {
        store.donateForm.calmMode = true;

        if (options['excludeFirstStep'] === true) {
          store.donateForm.calmModeExcludeFirstStep = true;
        }
      };
      window.enableCalmMode = enableCalmMode; // overwrites window.enableCalmMode defined in application.js

      if (window.calmModeEnabled) {
        // CALM mode was enabled before the DonateForm component was mounted
        enableCalmMode({ excludeFirstStep: window.calmModeExcludeFirstStep });
      }

      store.donateForm.disableAddressCollection =
        props.disableAddressCollection;
      store.donateForm.amount = props.amount;
      store.donateForm.campaignSlug = props.campaignSlug;
      store.donateForm.campaignPath = props.campaignPath;
      store.donateForm.campaignType = props.campaignType;
      store.donateForm.campaignId = props.campaignId;
      store.donateForm.embedded = props.embedded;
      store.donateForm.generalDonateCampaignId = props.generalDonateCampaignId;
      store.donateForm.campaignName = props.campaignName;
      store.donateForm.teamName = props.teamName;
      store.donateForm.profileOwnerName = props.profileOwnerName;
      store.donateForm.campaignerProfileImageTag =
        props.campaignerProfileImageTag;
      store.donateForm.claimedGiftAid = props.claimedGiftAid;
      store.donateForm.hasActiveGiftAid = props.hasActiveGiftAid;
      store.donateForm.isIho = props.acceptsIho;
      store.donateForm.onlyAcceptsIho = props.onlyAcceptsIho;
      store.donateForm.plaidEnv = props.plaidEnv;
      store.donateForm.showDonationFrequencyOption =
        props.showSubscriptionOption || props.showDonationFrequencyOption;
      store.donateForm.isReferral = props.isReferral;
      store.donateForm.isProfileDonation = props.isProfileDonation || false;
      store.donateForm.isMycwCampaign = props.isMycwCampaign;
      store.donateForm.referralSource = props.referralSource;
      store.donateForm.name = props.defaultName;
      store.donateForm.surname = props.defaultSurname;
      store.donateForm.email = props.email;
      store.donateForm.idempotencyKey = uuid();
      store.donateForm.isAnonymous = props.isAnonymous;
      store.donateForm.step = props.skipAmountStep ? 'payment' : 'amount';
      store.donateForm.country = props.campaignCountry;
      store.donateFormButtonAmounts.country = props.campaignCountry;
      store.donateForm.endpointPaths = props.endpointPaths;
      store.donateForm.customInfositeThankYouPagePath =
        props.customInfositeThankYouPagePath;
      store.donateForm.customBuilderThankYouPagePath =
        props.customBuilderThankYouPagePath;
      store.donateForm.customBuilderWpsThankYouPagePath =
        props.customBuilderWpsThankYouPagePath;
      store.donateForm.customInfositeOneTimeThankYouPageName =
        props.customInfositeOneTimeThankYouPageName;
      store.donateForm.customInfositeMonthlyThankYouPageName =
        props.customInfositeMonthlyThankYouPageName;
      store.donateForm.customInfositeWpsThankYouPageName =
        props.customInfositeWpsThankYouPageName;
      store.donateForm.sendDefaultWelcomeSeriesEmails =
        props.sendDefaultWelcomeSeriesEmails;
      store.donateForm.customWelcomeJourneyId = props.customWelcomeJourneyId;
      store.donateForm.customThankYouJourneyId = props.customThankYouJourneyId;

      store.donateForm.defaultSubscriptionPaymentMethodOnDesktopToBank =
        props.defaultSubscriptionPaymentMethodOnDesktopToBank;
      store.donateForm.defaultSubscriptionPaymentMethodOnMobileToBank =
        props.defaultSubscriptionPaymentMethodOnMobileToBank;
      store.donateForm.mostRecentOpenCampaign = props.mostRecentOpenCampaign;
      store.donateForm.profileOwnerImageTag = props.profileOwnerImageTag;
      store.donateForm.ukEuIp = props.ukEuIp;
      store.donateForm.emailConsentGranted =
        !props.isUserLoggedIn && props.emailConsentGranted;
      store.donateForm.mailConsentGranted = props.mailConsentGranted;
      store.donateForm.askForAddress = props.askForAddress;

      reaction(
        () => store.donateForm.productVariant,
        () => this.setProductVariant()
      );

      reaction(
        () => store.donateForm.optimizelyPaymentTab,
        () => store.donateForm.setSelectedPaymentTab()
      );

      reaction(
        () => store.donateForm.paymentTab,
        () => {
          if (store.donateForm.paymentTab == 'plaid') {
            this.loadPlaidJsScript();
          }
        }
      );

      reaction(
        () => store.donateForm.step,
        (step) => {
          store.donateForm.dispatchStepChangedEvent(step);
        }
      );

      store.donateForm.isUserLoggedIn = props.isUserLoggedIn;
      if (!props.managementMode) {
        store.donateForm.getStripePaymentMethodCards(); // dependent on isUserLoggedIn
      }
      store.donateForm.isAlreadySpringMember = props.isAlreadySpringMember;
      store.donateForm.phoneNumber.existingValue = props.existingPhoneNumber;
      store.donateForm.phoneNumberConsentGranted =
        props.phoneNumberConsentGranted;
      store.donateForm.isIndividualAccount = props.isIndividualAccount;
      store.donateForm.existingMembershipCountry =
        props.existingMembershipCountry;
      store.donateForm.queryParams = queryParams;
      store.donateFormButtonAmounts.queryParams = queryParams;
      store.donateFormButtonAmounts.customParams =
        props.infositeButtonAmounts || props.customButtonAmounts || {};
      store.donateForm.productVariant = queryParams.product;
      store.donateForm.customDefaultAmounts = Array.isArray(
        props.customDefaultAmounts
      )
        ? props.customDefaultAmounts[0]
        : props.customDefaultAmounts;
      store.donateForm.minAmount = props.minAmount;
      store.donateForm.subscriptionMinAmount = props.subscriptionMinAmount || 1;
      store.donateForm.managementMode = props.managementMode;
      store.donateForm.disableHeader = props.disableHeader;
      store.donateForm.disablePaypal = props.disablePaypal;
      store.donateForm.customDonateButtonText = props.customDonateButtonText;
      store.donateForm.disableMembershipCheck = props.disableMembershipCheck;
      store.donateForm.disablePhoneNumber = props.disablePhoneNumber;
      store.donateForm.isBpsDonateForm = props.isBpsDonateForm;
      store.donateForm.referer = props.referer;
      store.donateForm.simulateStripeWebhooks = props.simulateStripeWebhooks;
      store.donateForm.customNsIntegrationCodeFor1xDonations =
        props.customNsIntegrationCodeFor1xDonations;
      store.donateForm.customNsIntegrationCodeForWps1xDonations =
        props.customNsIntegrationCodeForWps1xDonations;
      store.donateForm.customNsIntegrationCodeForSubscriptionDonations =
        props.customNsIntegrationCodeForSubscriptionDonations;
      store.donateForm.customXeroIntegrationCodeFor1xDonations =
        props.customXeroIntegrationCodeFor1xDonations;
      store.donateForm.customXeroIntegrationCodeForWps1xDonations =
        props.customXeroIntegrationCodeForWps1xDonations;
      store.donateForm.customXeroIntegrationCodeForSubscriptionDonations =
        props.customXeroIntegrationCodeForSubscriptionDonations;
      if (props.donationKind) {
        store.donateForm.donationKind = props.donationKind;
      }
      store.donateForm.showAmountInput =
        props.showAmountInput || store.donateForm.isCalmMode;
      store.donateForm.subscriptionId = props.subscriptionId;
      store.donateForm.existingSubscriptionState =
        props.existingSubscriptionState;
      store.donateForm.hideWpsAmountButton = props.hideWpsAmountButton;
      store.donateForm.amountStepTextBlockMonthly =
        props.amountStepTextBlockMonthly;
      store.donateForm.amountStepTextBlock1xDonation =
        props.amountStepTextBlock1xDonation;
      store.donateForm.landingPageCampaignTag = props.landingPageCampaignTag;
      store.donateForm.eventKioskMode = props.eventKioskMode;

      if (typeof store.donateForm.isSubscription == 'undefined') {
        store.donateForm.isSubscription = props.isSubscription;
        this.setDefaultDonationFrequency();
      }

      if (props.currentPaymentMethod) {
        store.donateForm.currentPaymentMethod = props.currentPaymentMethod;
      }

      store.donateForm.setSelectedPaymentTab();

      store.donateForm.setCustomDefaultAmount();
      window.AmountButtonsExperiment = AmountButtonsExperiment;
      store.donateForm.kioskMode = props.kioskMode;
      if (props.isAnnual) {
        store.donateForm.isAnnual = true;
      }
      store.donateForm.subscriptionUpsell.sortedBrackets = props
        .subscriptionUpsellConfig?.brackets
        ? sortBy(props.subscriptionUpsellConfig.brackets, [
            (bracket) => bracket.minAmount,
          ]).reverse()
        : sortBy(props.subscriptionUpsellConfig, [
            (bracket) => bracket.minAmount,
          ]).reverse();
      store.donateForm.subscriptionUpsell.sortedBracketsMonthlyToAnnual = props
        .subscriptionUpsellConfigMonthlyToAnnual?.brackets
        ? sortBy(props.subscriptionUpsellConfigMonthlyToAnnual.brackets, [
            (bracket) => bracket.minAmount,
          ]).reverse()
        : sortBy(props.subscriptionUpsellConfigMonthlyToAnnual, [
            (bracket) => bracket.minAmount,
          ]).reverse();
      store.donateForm.profileCampaignId = props.profileCampaignId;
      store.donateForm.wpsAmount = props.wpsAmount;
      store.donateForm.wpsMinimumAmount = props.wpsMinimumAmount;
    }

    componentDidMount = () => {
      this.loadStripeJs();
      store.donateForm.disableFormSubmit = false;
      store.donateForm.isFormMounted = true;
      if (this.props.acceptsIho && store.iho.designs.length === 0)
        store.iho.setDesigns(this.props.ihoCardDesigns);

      intercept(store.donateForm, 'paymentTab', (change) => {
        if (store.donateForm.disableFormSubmit) {
          return null;
        }

        return change;
      });

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const newHeight = entry.target.scrollHeight;
          window.parent.postMessage({ height: newHeight }, '*');
        }
      });
      resizeObserver.observe(this.donateForm);

      window.addEventListener('message', (event) => {
        if (event.data.url) {
          store.donateForm.referer = event.data.url;
        }
      });
    };

    loadStripeJs = () => {
      stripePromise = window.useFakeStripe
        ? fakeStripe()
        : loadStripe(this.props.stripePublishableKey);
    };

    plaidJsScriptLoaded = () => document.getElementById('plaid-js');

    loadPlaidJsScript = () => {
      if (this.plaidJsScriptLoaded()) return null;
      if (window.useFakePlaid) return null;
      const script = document.createElement('script');
      script.defer = true;
      script.id = 'plaid-js';
      script.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
      document.head.appendChild(script);
    };

    setProductVariant = () => {
      switch (store.donateForm.productVariant) {
        case 'one-time':
          store.donateForm.setProductOneTimeOnly();
          break;
        case 'monthly':
          store.donateForm.setProductMonthlyOnly();
          break;
        case 'one-time-and-monthly':
          store.donateForm.setProductOneTimeAndMonthly();
          break;
        case 'iho':
          store.donateForm.setProductIho();
          break;
      }
    };

    setDefaultDonationFrequency = () => {
      if (this.defaultToMonthly()) {
        store.donateForm.isSubscription = true;
      } else if (queryParams.defaultToggle == 'one-time') {
        store.donateForm.isSubscription = false;
      }
    };

    defaultToMonthly = () =>
      (queryParams.defaultToggle == 'monthly' ||
        ((this.props.defaultToggle == 'monthly' ||
          this.props.defaultDonationFrequency == 'monthly') &&
          !queryParams.defaultToggle)) &&
      !store.donateForm.isAlreadySpringMember;

    showAmountInput = () =>
      store.donateForm.showAmountInput ||
      queryParams.variant == 'amount-input' ||
      ['water-project-sponsorship', 'water-project-sponsorship-uk'].includes(
        store.donateForm.campaignSlug
      ) ||
      store.donateForm.campaignType === 'user';

    isProfileReferral = () =>
      store.donateForm.referralSource &&
      store.donateForm.referralSource.sourceMechanism == 'public_profile';

    validateForm = () => $(this.form).parsley().validate();

    handleNextStepClick = () => {
      store.donateForm.disableFormSubmit = false;

      if (!this.validateForm()) {
        return false;
      }

      const currentStep = store.donateForm.step;
      let newStep;

      switch (currentStep) {
        case 'amount':
          if (store.donateForm.subscriptionUpsell.showUpsell()) {
            store.donateForm.tracking.trackDonateEvent('Upsell Viewed');

            newStep = 'subscription-upsell';
          } else {
            if (
              store.donateForm.onlyAcceptsIho &&
              !store.iho.honoreeInfoIsPresent
            ) {
              store.iho.modalOpen = true;
              return true;
            }

            newStep = this.shouldDisplayGiftAidStep() ? 'gift-aid' : 'payment';
          }

          break;
        case 'subscription-upsell':
          this.handleSubscriptionUpsellSelection();

          newStep = this.shouldDisplayGiftAidStep() ? 'gift-aid' : 'payment';

          break;
        case 'donor-info-collection':
        case 'gift-aid':
          newStep = 'payment';

          break;
        default:
          break;
      }

      if (
        (store.donateForm.isCalmMode ||
          (currentStep === 'amount' &&
            store.donateForm.calmModeExcludeFirstStep)) &&
        newStep === 'payment' &&
        store.donateForm.step !== 'donor-info-collection'
      ) {
        newStep = 'donor-info-collection';
        store.donateForm.tracking.trackViewedInfoCollectionStepEvent();
      }

      if (newStep === 'payment') {
        store.donateForm.tracking.trackCheckoutStartedEvent();

        if (store.donateForm.isCalmMode) {
          store.donateForm.tracking.trackContactInfoProvidedEvent();
        }
      }

      store.donateForm.step = newStep;
    };

    shouldDisplayGiftAidStep = () => !this.shouldNotDisplayGiftAidStep();

    shouldNotDisplayGiftAidStep = () =>
      store.donateForm.country === 'us' ||
      store.donateForm.managementMode ||
      store.donateForm.hasActiveGiftAid;

    handleSubscriptionUpsellSelection = () => {
      if (store.donateForm.subscriptionUpsell.isOptedIn) {
        store.donateForm.tracking.trackDonateEvent('Upsell Accepted');
      } else {
        store.donateForm.tracking.trackDonateEvent('Upsell Declined');
      }
    };

    trackPayButtonClick = (buttonTypeData) => {
      if (
        buttonTypeData.button_type === 'Payment Request Button' &&
        store.donateForm.isCalmMode
      ) {
        store.donateForm.tracking.trackCheckoutStartedEvent();
      }
      if (
        !store.donateForm.managementMode &&
        buttonTypeData.button_type !== 'Credit Card'
      ) {
        store.donateForm.tracking.trackContactInfoProvidedEvent();
      }
      store.donateForm.tracking.trackDonationFormSubmittedEvent();

      Sentry.withScope((scope) => {
        scope.setLevel('info');
        scope.setExtra('formData', store.donateForm.donationFormParams);
        Sentry.captureMessage('Donate Form Submitted');
      });
    };

    paypalTabClass = () => {
      if (store.donateForm.paymentTab !== 'pay_pal') return '';
      return store.donateForm.clickedPaypalSubmit ? 'loading' : 'active';
    };

    handleFormSubmitViaEnterKey = () => {
      if (store.donateForm.disableFormSubmit) return;
      store.donateForm.disableFormSubmit = true;
      store.donateForm.stripeSubmitViaEnterKeyHandler();
    };

    handlePaypalTabClick = (event) => {
      if (store.donateForm.isUserLoggedIn && !store.donateForm.managementMode) {
        store.donateForm.paymentTab = 'pay_pal';
        this.handlePaypalSubmitClick(event);
      } else {
        store.donateForm.paymentTab = 'pay_pal';
      }
    };

    handlePlaidTabClick = () => {
      store.donateForm.paymentTab = 'plaid';
    };

    handleBacsTabClick = () => {
      store.donateForm.paymentTab = 'bacs';
    };

    handleStripeTabClick = () => {
      if (this.emailField) {
        const parsley = $(this.emailField).parsley();

        if (store.donateForm.paymentTab == 'pay_pal' && !parsley.isValid()) {
          store.donateForm.creditCardStep = 1;
        }
      }

      store.donateForm.paymentTab = 'stripe';
    };

    handlePaypalSubmitSuccess = () => {
      store.donateForm.name = '';
      store.donateForm.surname = '';
      store.donateForm.clickedPaypalSubmit = true;

      this.trackPayButtonClick({
        button_type: 'Pay Pal',
      });

      store.donateForm.createDonationForm('paypal', ({ redirectUrl }) => {
        pauseForSpecs(() => {
          window.location = redirectUrl;
        });
      });
    };

    handleBacsSubmitClick = (event) => {
      event.preventDefault();

      if (!this.validateForm()) {
        return false;
      }

      store.donateForm.disableFormSubmit = true;
      store.donateForm.paymentTab = 'bacs';

      this.trackPayButtonClick({
        button_type: 'BACS',
      });

      store.donateForm.createDonationForm(
        'bacs',
        ({ redirectUrl }) => {
          pauseForSpecs(() => {
            window.location = redirectUrl;
          });
        },
        ({ existingMembershipCountry, isSpringMember }) => {
          if (isSpringMember) {
            store.donateForm.isAlreadySpringMember = true;
            store.donateForm.existingMembershipCountry =
              existingMembershipCountry;
          }
        }
      );
    };

    handlePaypalSubmitClick = (event) => {
      if (!this.validateForm()) {
        return false;
      }

      store.donateForm.disableFormSubmit = true;

      if (
        store.donateForm.isSubscription &&
        !store.donateForm.isAlreadySpringMember
      ) {
        event.preventDefault();
        $.getJSON(
          `/api/v1/spring_membership/${store.donateForm.sanitizedEmail}`,
          (data) => {
            store.donateForm.isAlreadySpringMember = data['is_spring_member'];
            store.donateForm.existingSubscriptionState =
              data['existing_subscription_state'];

            if (
              !store.donateForm.isAlreadySpringMember ||
              store.donateForm.existingSkippedOrFailedPaymentSubscription
            ) {
              this.handlePaypalSubmitSuccess();
            }
          }
        );
      } else {
        this.handlePaypalSubmitSuccess();
      }
    };

    renderStripePaymentRequestForm = () => (
      <Elements stripe={stripePromise}>
        <ElementsConsumer>
          {({ stripe }) => (
            <>
              {stripe && (
                <PaymentRequestForm
                  stripe={stripe}
                  trackPayButtonClick={this.trackPayButtonClick}
                  validateForm={this.validateForm}
                />
              )}
            </>
          )}
        </ElementsConsumer>
      </Elements>
    );

    renderBacsTab = () => (
      <>
        {!store.donateForm.isCalmMode && (
          <DonateFormDonorInfoPopoutForm
            namePlaceholder='First Name'
            surnamePlaceholder='Last Name'
          />
        )}

        <button
          className={cx('button w-full', {
            'calm-donate-form-button': store.donateForm.isCalmMode,
          })}
          id='donate-button'
          onClick={this.handleBacsSubmitClick}
        >
          {t('views.donate_form.submit_button_text')}
        </button>
        {store.donateForm.isMycwSubscription && (
          <p
            className='h80 text-grey-60 mt-2'
            dangerouslySetInnerHTML={{
              __html: t('views.donate_form.mycw_subscription_disclaimer'),
            }}
          />
        )}

        <div className='flex mt-2'>
          <img className='opacity-40 mr-2 w-16' src={directDebitLogo} />
          <p className='text-left leading-normal h80 text-grey-60'>
            You&#39;ll complete your payment with BACS Direct Debit on the next
            page.
          </p>
        </div>
      </>
    );

    renderPaypalTab = () => {
      if (store.donateForm.clickedPaypalSubmit) {
        return (
          <div className='paypal-tab' ref={(el) => (this.payPalTab = el)}>
            <p className='em mb-0 pb-3'>Sending you to PayPal…</p>
          </div>
        );
      }

      if (store.donateForm.managementMode) {
        return (
          <>
            <div className='p-2 bg-grey-5 mb-2'>
              <p className='text-small text-left mb-1 leading-normal text-center'>
                Thank you for being part of the Spring community! You&apos;ll
                need to visit your PayPal account directly to update your
                payment method.&nbsp;
                <a
                  href='https://support.charitywater.org/hc/en-us/articles/360023748952'
                  rel='noopener noreferrer'
                  target='_blank'
                >
                  Click here
                </a>{' '}
                for directions. You can also switch your payment method to a
                credit card or bank account, which will allow for seamless
                maintenance in the&nbsp;future.
              </p>
            </div>
            <a
              className='button w-full'
              href={store.donateForm.endpointPaths.paypal}
            >
              Visit PayPal
            </a>
            {store.donateForm.isMycwSubscription && (
              <p
                className='h80 text-grey-60 mt-2'
                dangerouslySetInnerHTML={{
                  __html: t('views.donate_form.mycw_subscription_disclaimer'),
                }}
              />
            )}
          </>
        );
      }

      return (
        <>
          {!store.donateForm.isCalmMode && (
            <DonateFormEmailField ref={(el) => (this.emailField = el)} />
          )}

          <button
            className={`button w-full ${
              store.donateForm.isCalmMode ? 'calm-donate-form-button' : ''
            }`}
            id='donate-button'
            onClick={this.handlePaypalSubmitClick}
          >
            {t('views.donate_form.submit_button_text')}
          </button>
        </>
      );
    };

    amountStepButtonText() {
      if (store.donateForm.isSubscription) {
        if (store.donateForm.isAnnual) {
          const defaultText = store.donateForm.isCalmMode
            ? t('views.donate_form.join_today')
            : t('views.donate_form.donate_annually');
          return this.props.donateAnnuallyCta || defaultText;
        } else {
          const defaultText = store.donateForm.isCalmMode
            ? t('views.donate_form.join_today')
            : t('views.donate_form.donate_monthly');
          return this.props.donateMonthlyCta || defaultText;
        }
      } else {
        if (this.props.donateOneTimeCta) {
          return this.props.donateOneTimeCta.replace(
            '%{amount}',
            `${store.donateForm.currencyUnit}${
              amountWithPrecisionAsRequiredAndComma(store.donateForm.amount) ||
              0
            }`
          );
        }

        if (store.donateForm.isWpsAmountButtonSelected) {
          return store.donateForm.donateButtonText;
        }

        return t('views.donate_form.donate');
      }
    }

    amountComponent = () => {
      if (this.props.showAmountInputWithAmountButtons) {
        return (
          <>
            <DonateFormAmountInput
              focus={this.props.focus}
              maxAmount={this.props.maxAmount}
              readOnly={this.props.readOnly}
              withAmountButtons={true}
            />
            <DonateFormAmountButtons
              key={store.donateForm.isSubscription}
              maxAmount={this.props.maxAmount}
              mini={true}
              readOnly={this.props.readOnly}
            />
          </>
        );
      } else if (this.showAmountInput()) {
        return (
          <DonateFormAmountInput
            focus={this.props.focus}
            maxAmount={this.props.maxAmount}
            readOnly={this.props.readOnly}
          />
        );
      } else {
        return (
          <DonateFormAmountButtons
            key={store.donateForm.isSubscription}
            maxAmount={this.props.maxAmount}
            readOnly={this.props.readOnly}
          />
        );
      }
    };

    shouldDisplayRecaptchaText = () =>
      store.donateForm.isCalmMode &&
      (store.donateForm.step === 'payment' ||
        (store.donateForm.supportsPaymentRequestButton &&
          store.donateForm.step === 'donor-info-collection'));

    donationType = () => {
      if (store.donateForm.isSubscription) {
        return 'monthly';
      } else {
        return 'one-time';
      }
    };

    render() {
      const { acceptsChecks, defaultComment, errors, fontStylesheetUrl } =
        this.props;

      const {
        amount,
        campaignName,
        claimedGiftAid,
        country,
        creditCardStep,
        currencyUnit,
        disableHeader,
        donateButtonText,
        embedded,
        formId,
        giftAidAmount,
        handleRecaptchaSuccess,
        isCalmMode,
        isFormMounted,
        isPaypalAvailable,
        isSubscription,
        isSystemCampaign,
        isUserLoggedIn,
        isWpsAmountButtonSelected,
        mostRecentOpenCampaign,
        paymentTab,
        profileCampaignId,
        showAmountStepTextBlock,
        showSpringMembershipAlreadyExistsMessage,
        showSubscriptionSavedPaymentMessage,
        step,
        subscriptionPeriod,
        subscriptionUpsell,
        tracking,
      } = store.donateForm;

      return (
        <div
          className={`donate-form-wrapper stripe-elements step-${step} theme-maji ${
            isCalmMode && 'calm-mode'
          }`}
          ref={(el) => {
            this.donateForm = el;
          }}
        >
          {step === 'amount' && mostRecentOpenCampaign && (
            <CampaignRedirectBanner />
          )}

          {!isCalmMode && (
            <DonateFormDonationFrequencyToggle
              monthlyToggleCtaText={this.props.monthlyToggleCtaText}
            />
          )}

          <form
            autoComplete='off'
            data-logged-in-user={isUserLoggedIn}
            data-parsley-validate={true}
            data-store-utm-params-model='donation_form'
            id={formId}
            noValidate={true}
            onClick={
              tracking.interactionStartedEventTracked
                ? null
                : tracking.trackInteractionStartedEvent
            }
            onKeyDown={
              tracking.interactionStartedEventTracked
                ? null
                : tracking.trackInteractionStartedEvent
            }
            onSubmit={this.handleFormSubmitViaEnterKey}
            ref={(el) => {
              this.form = el;
            }}
          >
            {!this.props.doNotRenderIhoModal && <DonateFormIhoModal />}

            {!disableHeader && (
              <DonateFormHeader
                allowAmountEdit={this.props.allowAmountEdit}
                amountStepDescription={this.props.amountStepDescription}
                amountStepHeader={this.props.amountStepHeader}
                paymentStepHeader={this.props.paymentStepHeader}
                showAmountInput={this.showAmountInput()}
                skipAmountStep={this.props.skipAmountStep}
              />
            )}

            <div
              className={cx(
                'donate-form-body',
                isCalmMode && 'calm-donate-form-body'
              )}
            >
              <RecaptchaBadge successCallback={handleRecaptchaSuccess} />
              <DonateFormIhoHonoree />
              {step === 'amount' && (
                <>
                  {this.amountComponent()}
                  {(!isSystemCampaign ||
                    this.isProfileReferral() ||
                    profileCampaignId) && (
                    <DonateFormUserCampaignFields
                      defaultComment={defaultComment}
                      errors={errors}
                    />
                  )}
                  <DonateFormIhoToggle validateForm={this.validateForm} />
                  {isCalmMode && (
                    <AmountImpactCalculator
                      amount={amount}
                      costPerPerson={this.props.costPerPerson}
                      currencyUnit={currencyUnit}
                      donationType={this.donationType()}
                      iconSize='16px'
                      wrapperClasses='donate-form-impact-calculator'
                    />
                  )}
                  <DonateFormButton
                    classes={`show-next-donate-screen ${
                      isCalmMode ? 'calm-donate-form-button' : ''
                    }`}
                    onClick={this.handleNextStepClick}
                    text={this.amountStepButtonText()}
                  />
                  {acceptsChecks && !isCalmMode && (
                    <div
                      className={cx('form-footer whitespace-nowrap', {
                        hidden: isSubscription,
                      })}
                    >
                      {country !== 'uk' && (
                        <DonateByCheck
                          campaignCheckCode={this.props.campaignCheckCode}
                          campaignName={campaignName}
                          isSystemCampaign={isSystemCampaign}
                        />
                      )}
                      <a className='donate-by-check h80' href='/crypto'>
                        {t('views.donate_form.give_crypto')}
                      </a>
                    </div>
                  )}
                </>
              )}
              {step === 'subscription-upsell' && (
                <>
                  <SubscriptionUpsell />
                  <DonateFormButton
                    classes={`show-next-donate-screen ${
                      isCalmMode ? 'calm-donate-form-button' : ''
                    }`}
                    onClick={this.handleNextStepClick}
                    text={
                      (subscriptionUpsell.isOptedIn &&
                        subscriptionUpsell.matchingBracket()?.options
                          ?.submitButtonText) ||
                      donateButtonText
                    }
                  />
                </>
              )}
              {step === 'gift-aid' && (
                <>
                  <div className='giftaid-logo mx-auto -mt-1' />
                  <p className='pb-2 px-1 text-center'>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t('views.donate_form.gift_aid.opt_in', {
                          amount:
                            amountWithPrecisionAsRequiredAndComma(
                              giftAidAmount
                            ),
                        }),
                      }}
                    />
                  </p>
                  <div className='bg-grey-10 rounded border-solid border border-grey-30 p-2 pb-1 mb-2 text-left'>
                    <div className='switch-wrapper mb-1'>
                      <label
                        className='label-switch small pr-1'
                        id='giftaid-switch'
                      >
                        <input
                          defaultChecked={claimedGiftAid}
                          onChange={() =>
                            (store.donateForm.claimedGiftAid =
                              !store.donateForm.claimedGiftAid)
                          }
                          type='checkbox'
                        />
                        <div className='checkbox' />
                      </label>
                      <label>Claim Gift Aid?</label>
                    </div>
                    <p className='text-small'>
                      By checking above, I confirm I am a UK taxpayer and I
                      would like charity: water to treat this donation as a Gift
                      Aid donation. I understand that if I pay less income tax
                      and/or Capital Gains tax than the amount of Gift Aid
                      claimed on all my donations in that year, it is my
                      responsibility to pay any&nbsp;difference.
                    </p>
                  </div>
                  <DonateFormButton
                    classes={`show-next-donate-screen ${
                      isCalmMode ? 'calm-donate-form-button' : ''
                    }`}
                    onClick={this.handleNextStepClick}
                    text='Next Step'
                  />
                </>
              )}
              {isCalmMode && step === 'donor-info-collection' && (
                <>
                  {!embedded && this.renderStripePaymentRequestForm()}
                  <DonateFormDonorInfoCollectionFields />
                  <DonateFormButton
                    classes='show-next-donate-screen calm-donate-form-button'
                    onClick={this.handleNextStepClick}
                    text='Next'
                  />
                </>
              )}
              <div
                className={cx({
                  hidden: step !== 'payment',
                })}
              >
                <>
                  {!showSpringMembershipAlreadyExistsMessage && (
                    <>
                      {!isCalmMode &&
                        step === 'payment' &&
                        isFormMounted &&
                        (paymentTab != 'stripe' || creditCardStep == 1) &&
                        this.renderStripePaymentRequestForm()}
                      {!isCalmMode && (
                        <div className='tab-wrapper'>
                          <nav className='sm-tabs tabs-mini'>
                            <ul>
                              <li
                                className={cx('stripe-donate-button', {
                                  active: paymentTab === 'stripe',
                                })}
                                onClick={this.handleStripeTabClick}
                              >
                                <a className='h80'>Card</a>
                              </li>
                              {isPaypalAvailable && (
                                <li
                                  className={`paypal-donate-button ${this.paypalTabClass()}`}
                                  onClick={this.handlePaypalTabClick}
                                >
                                  <a className='h80'>PayPal</a>
                                </li>
                              )}
                              {isSubscription && country == 'us' && (
                                <li
                                  className={cx('ach-donate-button', {
                                    active: paymentTab === 'plaid',
                                  })}
                                  onClick={this.handlePlaidTabClick}
                                >
                                  <a className='h80'>
                                    <span className='hidden md-inline-block'>
                                      {' '}
                                      Link{' '}
                                    </span>{' '}
                                    Bank
                                  </a>
                                </li>
                              )}
                              {country == 'uk' && (
                                <>
                                  <li
                                    className={cx('bacs-donate-button', {
                                      active: paymentTab === 'bacs',
                                    })}
                                    onClick={this.handleBacsTabClick}
                                  >
                                    <a className='h80'>
                                      <span className='hidden md-inline-block'>
                                        {' '}
                                        {isSubscription
                                          ? 'Direct'
                                          : 'Bank'}{' '}
                                      </span>{' '}
                                      {isSubscription ? 'Debit' : 'Transfer'}
                                    </a>
                                  </li>
                                </>
                              )}
                            </ul>
                          </nav>
                        </div>
                      )}
                      {isCalmMode && (
                        <CalmPaymentTabs
                          country={country}
                          handleBacsTabClick={this.handleBacsTabClick}
                          handlePaypalTabClick={this.handlePaypalTabClick}
                          handlePlaidTabClick={this.handlePlaidTabClick}
                          handleStripeTabClick={this.handleStripeTabClick}
                          isSubscription={isSubscription}
                          paypalTabClass={this.paypalTabClass}
                        />
                      )}
                    </>
                  )}

                  {isFormMounted && (
                    <Elements
                      fonts={[{ cssSrc: fontStylesheetUrl }]}
                      stripe={stripePromise}
                    >
                      <ElementsConsumer>
                        {({ elements, stripe }) => (
                          <>
                            {elements && stripe && (
                              <StripeElements
                                donateForm={this.form}
                                elements={elements}
                                stripe={stripe}
                                trackPayButtonClick={this.trackPayButtonClick}
                                validateForm={this.validateForm}
                              />
                            )}
                          </>
                        )}
                      </ElementsConsumer>
                    </Elements>
                  )}

                  {showSpringMembershipAlreadyExistsMessage ? (
                    <DonateFormSpringMemberExistsMessage />
                  ) : (
                    <>
                      {
                        {
                          plaid: (
                            <AchPopoutForm
                              trackPayButtonClick={this.trackPayButtonClick}
                              validateForm={this.validateForm}
                            />
                          ),
                          pay_pal: this.renderPaypalTab(),
                          bacs: this.renderBacsTab(),
                        }[paymentTab]
                      }
                    </>
                  )}
                </>
              </div>
              {step === 'embed-success' && (
                <>
                  <img
                    className='mb-2 mt-1 mx-auto'
                    src={EmbedThankYouSticker}
                    width='58'
                  />
                  <h1 className='h50 font-heading'>
                    Thank you for your&nbsp;
                    {currencyUnit}
                    {amountWithPrecisionAsRequiredAndComma(amount)}
                    {isSubscription && <>&nbsp;{subscriptionPeriod}</>}
                    &nbsp;gift!
                  </h1>
                  <p className='mt-1 body-l'>
                    100% of your donation will help bring clean water around the
                    world.
                  </p>
                </>
              )}
            </div>
            {step === 'amount' && !isCalmMode && showAmountStepTextBlock && (
              <AmountStepText costPerPerson={this.props.costPerPerson} />
            )}
            {step === 'amount' && isWpsAmountButtonSelected && (
              <div className='border-0 border-dashed border-grey-30 border-t leading-normal p-2 text-cw-black text-xs'>
                <p>
                  Bring clean water to an entire community or school. To learn
                  more,{' '}
                  <a
                    className='font-medium'
                    href={`${
                      country === 'uk' ? '/uk' : ''
                    }/donate/water-project-sponsorship`}
                  >
                    click&nbsp;here
                  </a>
                  .
                </p>
              </div>
            )}
            {showSubscriptionSavedPaymentMessage && (
              <div className='p-2 border-dashed border-0 border-t border-grey-30 flex leading-normal'>
                <img src={CreditCardIcon} />
                <p className='pl-1 text-cw-black text-left text-xs'>
                  We&apos;ll store your payment method through Stripe&apos;s
                  secure platform. You&apos;ll still be able to change your
                  linked payment at any time to ensure your giving
                  goes&nbsp;uninterrupted!
                </p>
              </div>
            )}
            {this.shouldDisplayRecaptchaText() && (
              <RecaptchaText calmMode={true} classes='pt-1 mx-2 md-mx-3'>
                <i className='cw-icon-lock' /> Secure Payment &#183;{' '}
              </RecaptchaText>
            )}
            {isCalmMode && step === 'payment' && (
              <ProcessedByText provider={paymentTab} />
            )}
          </form>
          {!isCalmMode && (
            <RecaptchaText classes='pt-1 mx-2 md-mx-3'>
              <i className='cw-icon-lock' /> Secure Payment &#183;{' '}
            </RecaptchaText>
          )}
        </div>
      );
    }
  }
);

export default DonateForm;
